/**
 * WorkflowObserverPage — PII-free workflow primitive visibility.
 *
 * When mounted, sets the x-ferp-observer-mode header on all GraphQL requests.
 * Backend requires BOTH the header AND workflow:observe scope (dual-gate).
 *
 * What's visible: Workflow IDs, status, state transitions, task names/types,
 * approval decisions, timestamps, duration, dependencies, SLA indicators.
 *
 * What's redacted: input_data, output_data, context_variables, metadata,
 * notes, comments — all blanked or replaced with "***redacted***".
 *
 * Security: RequireArchitect guard + dual-gate observer mode.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Container,
  Title,
  Text,
  Alert,
  Badge,
  Group,
  Stack,
  Paper,
  Select,
  Loader,
  Anchor,
} from '@mantine/core';
import {
  IconEyeOff,
  IconShieldCheck,
  IconAlertTriangle,
  IconArrowLeft,
  IconGitBranch,
} from '@tabler/icons-react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  MasterWorkflowDashboard,
  useProgramVariants,
  useMasterWorkflowConfig,
  type MnDhsProgram,
} from '@ferp/workflow-forms';
import { useTenantId } from '@/auth';
import { setObserverMode } from '@/utils/apollo-client';

export default function WorkflowObserverPage(): React.ReactElement {
  const tenantId = useTenantId() ?? 'default';
  const [searchParams] = useSearchParams();
  // DRR-132: context link back to definition diagram when arriving from WorkflowDetailPage
  const definitionId = searchParams.get('definitionId');

  const [selectedProgram, setSelectedProgram] = useState<MnDhsProgram | null>(null);
  const [observerInstanceId, setObserverInstanceId] = useState<string | null>(null);

  // Load available program variants
  const { variants, loading: variantsLoading, error: variantsError } = useProgramVariants(tenantId);

  // Load workflow config for selected program
  const {
    config,
    loading: configLoading,
  } = useMasterWorkflowConfig({
    tenantId,
    programVariant: selectedProgram ?? '',
  });

  // Enable observer mode on mount, disable on unmount
  useEffect(() => {
    setObserverMode(true);
    return () => {
      setObserverMode(false);
    };
  }, []);

  const handleProgramChange = useCallback((value: string | null) => {
    setSelectedProgram(value as MnDhsProgram | null);
    setObserverInstanceId(null);
  }, []);

  const subWorkflowCount = config?.subWorkflows?.length ?? 0;

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        {/* Observer Mode Banner */}
        <Alert
          icon={<IconEyeOff size={20} />}
          color="violet"
          variant="filled"
          radius="md"
        >
          <Group gap="xs" align="center">
            <Text fw={700} size="sm">OBSERVER MODE</Text>
            <Badge color="white" variant="light" size="sm">PII Redacted</Badge>
          </Group>
          <Text size="xs" mt={4}>
            All personally identifiable information is redacted. You can see workflow structure,
            status transitions, and timing without exposure to patient or user data.
            Enforced by IAM scope (workflow:observe) + request header (dual-gate).
          </Text>
        </Alert>

        {/* DRR-132: context bar when opened from WorkflowDetailPage */}
        {definitionId && (
          <Paper p="xs" withBorder radius="md" bg="violet.0">
            <Group gap="xs">
              <IconGitBranch size={14} color="var(--mantine-color-violet-6)" />
              <Text size="sm" c="violet.7">
                Observing instances of definition:
              </Text>
              <Badge variant="outline" color="violet" size="sm" ff="monospace">
                {definitionId}
              </Badge>
              <Anchor
                component={Link}
                to={`/ferp-core/workflows/${definitionId}`}
                size="sm"
                c="violet.6"
              >
                <Group gap={4}>
                  <IconArrowLeft size={12} />
                  View definition diagram
                </Group>
              </Anchor>
            </Group>
          </Paper>
        )}

        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={2}>Workflow Observer</Title>
            <Text size="sm" c="dimmed">
              Validate workflow execution without PII exposure
            </Text>
          </div>
          <Group gap="xs">
            <Badge leftSection={<IconShieldCheck size={12} />} color="violet" variant="light">
              Read-Only
            </Badge>
            {selectedProgram && !configLoading && (
              <Badge color="gray" variant="light">
                {subWorkflowCount} sub-workflow{subWorkflowCount !== 1 ? 's' : ''}
              </Badge>
            )}
          </Group>
        </Group>

        {/* Redaction Legend */}
        <Paper p="sm" withBorder radius="md" bg="gray.0">
          <Group gap="lg">
            <Group gap="xs">
              <Badge size="xs" color="green" variant="filled">Visible</Badge>
              <Text size="xs" c="dimmed">IDs, status, timestamps, task names, approval decisions</Text>
            </Group>
            <Group gap="xs">
              <Badge size="xs" color="red" variant="filled">Redacted</Badge>
              <Text size="xs" c="dimmed">input_data, output_data, notes, metadata</Text>
            </Group>
          </Group>
        </Paper>

        {/* Program Variant Selection */}
        <Paper p="md" withBorder radius="md">
          <Stack gap="sm">
            <Text fw={500}>Select Program Variant to Observe</Text>
            {variantsLoading ? (
              <Group gap="xs">
                <Loader size="xs" />
                <Text size="sm" c="dimmed">Loading program variants...</Text>
              </Group>
            ) : variantsError ? (
              <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light">
                {variantsError.message}
              </Alert>
            ) : (
              <Select
                placeholder="Choose a program variant"
                value={selectedProgram}
                onChange={handleProgramChange}
                data={variants.map((v) => ({
                  value: v.variant,
                  label: v.name,
                }))}
                style={{ maxWidth: 400 }}
              />
            )}
          </Stack>
        </Paper>

        {/* Workflow Config Summary (read-only view) */}
        {selectedProgram && configLoading && (
          <Group justify="center" py="xl">
            <Loader size="md" />
            <Text c="dimmed">Loading workflow configuration...</Text>
          </Group>
        )}

        {selectedProgram && config && !configLoading && (
          <Paper p="md" withBorder radius="md">
            <Stack gap="sm">
              <Text fw={500}>Workflow Structure: {selectedProgram}</Text>
              <Text size="sm" c="dimmed">
                {subWorkflowCount} sub-workflows configured.
                Select an active instance below to observe execution.
              </Text>
              {config.subWorkflows.map((sw) => (
                <Paper key={sw.workflowName} p="xs" bg="gray.0" radius="sm">
                  <Group justify="space-between">
                    <Group gap="xs">
                      <Badge size="xs" color={sw.color ?? 'blue'} variant="light">
                        {sw.criticality}
                      </Badge>
                      <Text size="sm">{sw.displayName}</Text>
                    </Group>
                    <Badge size="xs" variant="outline" color="gray">
                      {sw.executionMode}
                    </Badge>
                  </Group>
                </Paper>
              ))}
            </Stack>
          </Paper>
        )}

        {/* Master Workflow Dashboard — shown when an instance is selected */}
        {observerInstanceId && selectedProgram && (
          <MasterWorkflowDashboard
            masterInstanceId={observerInstanceId}
            programVariant={selectedProgram}
            tenantId={tenantId}
            compact
          />
        )}

        {/* Placeholder when no instance is active */}
        {selectedProgram && !observerInstanceId && config && (
          <Alert color="violet" variant="light">
            Workflow structure loaded. When active workflow instances exist,
            they will appear here for observation with PII redacted.
          </Alert>
        )}
      </Stack>
    </Container>
  );
}
